// src/analytics.ts
import { z as z4 } from "zod";

// src/game.ts
import { z as z3 } from "zod";

// src/ids.ts
import { z } from "zod";
var StableIdSchema = z.string().min(1).max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
var OpaqueIdSchema = z.string().uuid();
var LobbyCodeSchema = z.string().length(6).regex(/^[A-Z2-9]+$/);
var GuestNameSchema = z.string().trim().min(2).max(24);
var ClientCommandIdSchema = z.string().uuid();
var SessionTokenSchema = z.string().min(32).max(256);

// src/politics.ts
import { z as z2 } from "zod";
var PoliticsFactNameSchema = z2.enum([
  "atWar",
  "declaredWarLastTurn",
  "madePeaceLastTurn",
  "formedAllianceLastTurn",
  "formedTradeLastTurn",
  "lostCitiesLastTurn",
  "capturedCitiesLastTurn",
  "capitalLost",
  "blockadedCitiesLastTurn",
  "treasuryBelow",
  "overCapacity",
  "controlledBorderCities",
  "turnNumber"
]);
var PoliticsFactValueSchema = z2.union([
  z2.boolean(),
  z2.number().finite(),
  z2.array(z2.union([z2.boolean(), z2.number().finite()]))
]);
var PoliticsComparisonSchema = z2.object({
  fact: PoliticsFactNameSchema,
  op: z2.enum(["eq", "neq", "gt", "gte", "lt", "lte", "in"]),
  value: PoliticsFactValueSchema
}).strict();
var PoliticsPredicateSchema = z2.lazy(
  () => z2.union([
    PoliticsComparisonSchema,
    z2.object({ all: z2.array(PoliticsPredicateSchema).min(1) }).strict(),
    z2.object({ any: z2.array(PoliticsPredicateSchema).min(1) }).strict(),
    z2.object({ not: PoliticsPredicateSchema }).strict()
  ])
);
var PoliticsTargetSchema = z2.object({
  kind: z2.enum([
    "country",
    "capital",
    "selectedControlledCity",
    "selectedControlledBorderCity",
    "allControlledCities"
  ])
}).strict();
var MultiplierSchema = z2.number().finite().min(0.5).max(1.5);
var PoliticsEffectSchema = z2.discriminatedUnion("op", [
  z2.object({ op: z2.literal("addPopulation"), value: z2.number().int().min(1).max(10) }).strict(),
  z2.object({ op: z2.literal("addTreasury"), value: z2.number().int().min(-100).max(100) }).strict(),
  z2.object({ op: z2.literal("addFortressLevel"), value: z2.number().int().min(1).max(3) }).strict(),
  z2.object({
    op: z2.literal("multiplyUnitCost"),
    unit: z2.enum(["infantry", "armor", "air", "navy"]),
    value: MultiplierSchema
  }).strict(),
  z2.object({ op: z2.literal("multiplyIncome"), value: MultiplierSchema }).strict(),
  z2.object({
    op: z2.literal("multiplyAttack"),
    scope: z2.enum(["land", "all"]),
    value: MultiplierSchema
  }).strict(),
  z2.object({
    op: z2.literal("multiplyDefense"),
    scope: z2.enum(["land", "all", "adjacentToAlly"]),
    value: MultiplierSchema
  }).strict(),
  z2.object({ op: z2.literal("multiplyDevelopmentCost"), value: MultiplierSchema }).strict(),
  z2.object({ op: z2.literal("multiplyPopulationGrowth"), value: MultiplierSchema }).strict(),
  z2.object({ op: z2.literal("addReputation"), value: z2.number().int().min(-100).max(100) }).strict(),
  z2.object({ op: z2.literal("restoreDevelopment"), value: z2.number().int().min(1).max(3) }).strict()
]);
var PoliticsCardSchema = z2.object({
  schemaVersion: z2.literal(1),
  id: StableIdSchema,
  version: z2.number().int().positive(),
  titleKey: z2.string().min(1),
  bodyKey: z2.string().min(1),
  duration: z2.discriminatedUnion("kind", [
    z2.object({ kind: z2.literal("immediate") }).strict(),
    z2.object({ kind: z2.literal("turns"), turns: z2.union([z2.literal(1), z2.literal(3)]) }).strict()
  ]),
  eligibility: PoliticsPredicateSchema,
  weight: z2.number().int().positive(),
  cooldownTurns: z2.number().int().min(0),
  maxSelectionsPerMatch: z2.number().int().positive().optional(),
  target: PoliticsTargetSchema,
  effects: z2.array(PoliticsEffectSchema).min(1),
  tags: z2.array(StableIdSchema)
}).strict();
var PoliticsCatalogSchema = z2.array(PoliticsCardSchema).min(1).superRefine((cards, ctx) => {
  const keys = /* @__PURE__ */ new Set();
  for (const card of cards) {
    const key = `${card.id}:${card.version}`;
    if (keys.has(key))
      ctx.addIssue({ code: "custom", message: `duplicate politics card ${key}` });
    keys.add(key);
  }
});
var PoliticsFactsSchema = z2.object({
  atWar: z2.boolean(),
  declaredWarLastTurn: z2.boolean(),
  madePeaceLastTurn: z2.boolean(),
  formedAllianceLastTurn: z2.boolean(),
  formedTradeLastTurn: z2.boolean(),
  lostCitiesLastTurn: z2.number().int().min(0),
  capturedCitiesLastTurn: z2.number().int().min(0),
  capitalLost: z2.boolean(),
  blockadedCitiesLastTurn: z2.number().int().min(0),
  treasuryBelow: z2.number().int().min(0),
  overCapacity: z2.boolean(),
  controlledBorderCities: z2.number().int().min(0),
  turnNumber: z2.number().int().positive()
}).strict();

// src/game.ts
var RULES_VERSION = "0.2.0";
var PROTOCOL_VERSION = 9;
var MatchPhaseSchema = z3.enum(["planning", "locked", "resolving", "playback", "finished"]);
var OccupationStageSchema = z3.enum(["integrated", "captured", "integrating"]);
var UnitTypeSchema = z3.enum(["infantry", "armor", "air", "navy"]);
var LandUnitTypeSchema = z3.enum(["infantry", "armor"]);
var AirMissionTypeSchema = z3.enum(["support", "bombard", "rebase"]);
var NavyMissionTypeSchema = z3.enum([
  "support",
  "amphibious-support",
  "bombard",
  "blockade",
  "rebase"
]);
var LandUnitsSchema = z3.object({ infantry: z3.number().int().min(0), armor: z3.number().int().min(0) }).strict();
var UnitCountsSchema = LandUnitsSchema.extend({
  air: z3.number().int().min(0),
  navy: z3.number().int().min(0)
}).strict();
var DiplomacyAgreementSchema = z3.enum(["nap", "trade", "alliance"]);
var DiplomaticRelationSchema = z3.object({
  a: StableIdSchema,
  b: StableIdSchema,
  atWar: z3.boolean(),
  agreements: z3.array(DiplomacyAgreementSchema)
}).strict();
var DiplomacyProposalSchema = z3.object({
  proposalId: OpaqueIdSchema,
  fromCountryId: StableIdSchema,
  toCountryId: StableIdSchema,
  agreement: DiplomacyAgreementSchema,
  turnNumber: z3.number().int().positive()
}).strict();
var PoliticsOfferSchema = z3.object({
  countryId: StableIdSchema,
  turnNumber: z3.number().int().positive(),
  cardIds: z3.array(StableIdSchema).max(3),
  selectedCardId: StableIdSchema.nullable(),
  selectedTargetCityId: StableIdSchema.nullable()
}).strict();
var ActivePoliticsCardSchema = z3.object({
  countryId: StableIdSchema,
  cardId: StableIdSchema,
  cardVersion: z3.number().int().positive(),
  selectedTurn: z3.number().int().positive(),
  expiresAfterTurn: z3.number().int().positive(),
  effects: z3.array(PoliticsEffectSchema).min(1)
}).strict();
var PoliticsHistoryEntrySchema = z3.object({
  countryId: StableIdSchema,
  cardId: StableIdSchema,
  selectedTurn: z3.number().int().positive()
}).strict();
var GameModeSchema = z3.discriminatedUnion("kind", [
  z3.object({
    kind: z3.literal("turn-limit"),
    turnLimit: z3.union([z3.literal(10), z3.literal(15), z3.literal(20), z3.literal(30)])
  }).strict(),
  z3.object({ kind: z3.literal("world-domination") }).strict()
]);
var VictoryStandingSchema = z3.object({
  rank: z3.number().int().positive(),
  countryId: StableIdSchema,
  eligible: z3.boolean(),
  eliminated: z3.boolean(),
  controlledCities: z3.number().int().min(0),
  development: z3.number().int().min(0),
  military: z3.number().int().min(0),
  population: z3.number().int().min(0),
  diplomacy: z3.number().int().min(0).max(20),
  treasury: z3.number().int().min(0),
  reputation: z3.number().int().min(0).max(100),
  scoreMilli: z3.number().int().min(0).max(1e5)
}).strict();
var MatchOutcomeSchema = z3.object({
  reason: z3.enum(["turn-limit", "world-domination"]),
  winnerCountryId: StableIdSchema,
  standings: z3.array(VictoryStandingSchema).min(1)
}).strict();
var MatchPlayerSchema = z3.object({
  playerId: OpaqueIdSchema,
  name: z3.string().min(1).max(24),
  countryId: StableIdSchema,
  ready: z3.boolean(),
  connected: z3.boolean(),
  eliminated: z3.boolean(),
  treasury: z3.number().int().min(0),
  reputation: z3.number().int().min(0).max(100),
  diplomacyPoints: z3.number().int().min(0),
  breachFreeTurns: z3.number().int().min(0),
  breachedAgreementThisTurn: z3.boolean(),
  politicsFacts: PoliticsFactsSchema
}).strict();
var CityStateSchema = z3.object({
  cityId: StableIdSchema,
  ownerCountryId: StableIdSchema,
  units: UnitCountsSchema,
  development: z3.number().int().min(1),
  damagedDevelopmentCeiling: z3.number().int().min(1),
  population: z3.number().int().min(1),
  populationProgressBps: z3.number().int().min(0).max(9999),
  neutralDevelopmentProgressBps: z3.number().int().min(0).max(9999),
  fortress: z3.number().int().min(0).max(3),
  occupationStage: OccupationStageSchema
}).strict();
var MatchStateSchema = z3.object({
  matchId: OpaqueIdSchema,
  mapId: StableIdSchema,
  rulesVersion: z3.literal(RULES_VERSION),
  gameMode: GameModeSchema,
  turnNumber: z3.number().int().positive(),
  phase: MatchPhaseSchema,
  players: z3.array(MatchPlayerSchema).min(1),
  cities: z3.array(CityStateSchema).min(1),
  diplomacy: z3.array(DiplomaticRelationSchema),
  diplomacyProposals: z3.array(DiplomacyProposalSchema),
  politicsOffers: z3.array(PoliticsOfferSchema),
  activePolitics: z3.array(ActivePoliticsCardSchema),
  politicsHistory: z3.array(PoliticsHistoryEntrySchema),
  outcome: MatchOutcomeSchema.nullable()
}).strict();
var OrderBaseShape = {
  clientCommandId: ClientCommandIdSchema,
  replacementKey: StableIdSchema,
  matchId: OpaqueIdSchema,
  playerId: OpaqueIdSchema,
  countryId: StableIdSchema,
  turnNumber: z3.number().int().positive()
};
var MoveOrderSchema = z3.object({
  ...OrderBaseShape,
  kind: z3.literal("move"),
  originCityId: StableIdSchema,
  targetCityId: StableIdSchema,
  intermediateCityId: StableIdSchema.nullable(),
  units: LandUnitsSchema
}).strict();
var ProduceOrderSchema = z3.object({
  ...OrderBaseShape,
  kind: z3.literal("produce"),
  cityId: StableIdSchema,
  unitType: UnitTypeSchema,
  count: z3.number().int().positive().max(1e3)
}).strict();
var DevelopOrderSchema = z3.object({ ...OrderBaseShape, kind: z3.literal("develop"), cityId: StableIdSchema }).strict();
var FortifyOrderSchema = z3.object({ ...OrderBaseShape, kind: z3.literal("fortify"), cityId: StableIdSchema }).strict();
var RetreatOrderSchema = z3.object({
  ...OrderBaseShape,
  kind: z3.literal("retreat"),
  cityId: StableIdSchema,
  preferredCityId: StableIdSchema
}).strict();
var AirMissionOrderSchema = z3.object({
  ...OrderBaseShape,
  kind: z3.literal("air-mission"),
  originCityId: StableIdSchema,
  targetCityId: StableIdSchema,
  mission: AirMissionTypeSchema,
  count: z3.number().int().positive().max(1e3)
}).strict();
var NavyMissionOrderSchema = z3.object({
  ...OrderBaseShape,
  kind: z3.literal("navy-mission"),
  originCityId: StableIdSchema,
  targetCityId: StableIdSchema,
  mission: NavyMissionTypeSchema,
  count: z3.number().int().positive().max(1e3)
}).strict();
var OrderSchema = z3.discriminatedUnion("kind", [
  MoveOrderSchema,
  ProduceOrderSchema,
  DevelopOrderSchema,
  FortifyOrderSchema,
  RetreatOrderSchema,
  AirMissionOrderSchema,
  NavyMissionOrderSchema
]);
var AuthoritativeEventBaseSchema = z3.object({
  eventId: z3.string().min(1),
  index: z3.number().int().min(0),
  turnNumber: z3.number().int().positive(),
  cityId: StableIdSchema,
  visibilityCountryIds: z3.array(StableIdSchema)
});
var ReinforcementEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("reinforcement"),
  countryId: StableIdSchema,
  originCityId: StableIdSchema,
  units: LandUnitsSchema
}).strict();
var BattleSideSchema = z3.object({
  countryId: StableIdSchema,
  role: z3.enum(["attacker", "defender"]),
  committed: LandUnitsSchema,
  basePowerMilli: z3.number().int().min(0),
  factorBps: z3.number().int().min(9e3).max(11e3),
  modifiedPower: z3.number().int().min(0),
  casualties: LandUnitsSchema,
  survivors: LandUnitsSchema,
  retreatCityId: StableIdSchema.nullable(),
  surrendered: z3.boolean()
}).strict();
var BattleEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("battle"),
  battleKind: z3.enum(["meeting", "city"]),
  fortressLevel: z3.number().int().min(0).max(3),
  sides: z3.array(BattleSideSchema).min(2),
  winnerCountryId: StableIdSchema.nullable(),
  previousOwnerCountryId: StableIdSchema,
  resultingOwnerCountryId: StableIdSchema
}).strict();
var CaptureEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("capture"),
  previousCountryId: StableIdSchema,
  newCountryId: StableIdSchema
}).strict();
var ProductionEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("production"),
  countryId: StableIdSchema,
  unitType: UnitTypeSchema,
  count: z3.number().int().positive(),
  treasuryCost: z3.number().int().min(0),
  populationCost: z3.number().int().min(0)
}).strict();
var DevelopmentEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("development"),
  countryId: StableIdSchema,
  treasuryCost: z3.number().int().min(0),
  newDevelopment: z3.number().int().min(1)
}).strict();
var FortificationEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("fortification"),
  countryId: StableIdSchema,
  treasuryCost: z3.number().int().min(0),
  newFortress: z3.number().int().min(1).max(3)
}).strict();
var RefundEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("refund"),
  countryId: StableIdSchema,
  orderKind: z3.enum(["produce", "develop", "fortify"]),
  treasury: z3.number().int().min(0),
  population: z3.number().int().min(0)
}).strict();
var IncomeEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("income"),
  countryId: StableIdSchema,
  grossIncome: z3.number().int().min(0),
  capitalMultiplierBps: z3.number().int().min(1e4),
  tradeMultiplierBps: z3.number().int().min(1e4).max(11500),
  politicsMultiplierBps: z3.number().int().min(7500).max(12500),
  turnIncome: z3.number().int().min(0),
  treasuryAfter: z3.number().int().min(0)
}).strict();
var EliminationEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("elimination"),
  countryId: StableIdSchema
}).strict();
var MissionBattleEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("mission-battle"),
  domain: z3.enum(["air", "navy"]),
  sides: z3.array(
    z3.object({
      countryId: StableIdSchema,
      committed: z3.number().int().positive(),
      factorBps: z3.number().int().min(9e3).max(11e3),
      casualties: z3.number().int().min(0),
      survivors: z3.number().int().min(0)
    }).strict()
  ),
  winnerCountryId: StableIdSchema.nullable()
}).strict();
var MissionEventSchema = AuthoritativeEventBaseSchema.extend({
  type: z3.literal("mission"),
  domain: z3.enum(["air", "navy"]),
  countryId: StableIdSchema,
  mission: z3.enum(["support", "amphibious-support", "bombard", "blockade", "rebase"]),
  survivors: z3.number().int().min(0),
  hits: z3.number().int().min(0).max(2)
}).strict();
var AuthoritativeEventSchema = z3.discriminatedUnion("type", [
  ReinforcementEventSchema,
  BattleEventSchema,
  CaptureEventSchema,
  ProductionEventSchema,
  DevelopmentEventSchema,
  FortificationEventSchema,
  RefundEventSchema,
  IncomeEventSchema,
  EliminationEventSchema,
  MissionBattleEventSchema,
  MissionEventSchema
]);
var PlayerEventSchema = z3.discriminatedUnion("type", [
  ReinforcementEventSchema.omit({ visibilityCountryIds: true }),
  BattleEventSchema.omit({ visibilityCountryIds: true }),
  CaptureEventSchema.omit({ visibilityCountryIds: true }),
  ProductionEventSchema.omit({ visibilityCountryIds: true }),
  DevelopmentEventSchema.omit({ visibilityCountryIds: true }),
  FortificationEventSchema.omit({ visibilityCountryIds: true }),
  RefundEventSchema.omit({ visibilityCountryIds: true }),
  IncomeEventSchema.omit({ visibilityCountryIds: true }),
  EliminationEventSchema.omit({ visibilityCountryIds: true }),
  MissionBattleEventSchema.omit({ visibilityCountryIds: true }),
  MissionEventSchema.omit({ visibilityCountryIds: true })
]);

// src/analytics.ts
var ANALYTICS_CATALOG_VERSION = 1;
var ANALYTICS_CONSENT_POLICY_VERSION = "2026-08-30";
var ANALYTICS_MAX_BATCH_EVENTS = 20;
var AnalyticsEnvironmentSchema = z4.enum(["production", "staging", "development", "test"]);
var AnalyticsPlatformSchema = z4.enum(["web", "android", "ios"]);
var AnalyticsAcquisitionSourceSchema = z4.enum([
  "direct",
  "invite",
  "organic_store",
  "campaign",
  "unknown"
]);
var SemanticVersionSchema = z4.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/);
var ControlledSlugSchema = z4.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);
var DurationSchema = z4.number().int().min(0).max(864e5);
var CountSchema = z4.number().int().min(0).max(1e4);
var ProductAnalyticsErrorCodeSchema = z4.enum([
  "command_invalid_payload",
  "command_not_authenticated",
  "command_not_authorized",
  "command_not_found",
  "command_conflict",
  "command_invalid_phase",
  "command_invalid_order",
  "command_duplicate",
  "command_internal",
  "network_timeout",
  "realtime_error",
  "session_invalid"
]);
var AnalyticsClientContextSchema = z4.object({
  analyticsSubjectId: z4.string().uuid(),
  analyticsSessionId: z4.string().uuid(),
  identityKind: z4.literal("guest"),
  platform: AnalyticsPlatformSchema,
  locale: ControlledSlugSchema,
  acquisitionSource: AnalyticsAcquisitionSourceSchema,
  campaignKey: ControlledSlugSchema.optional(),
  appVersion: SemanticVersionSchema,
  protocolVersion: z4.literal(PROTOCOL_VERSION)
}).strict();
var AnalyticsMatchContextSchema = z4.object({
  analyticsMatchId: z4.string().uuid(),
  rulesVersion: SemanticVersionSchema,
  mapDigest: z4.string().min(1).max(128),
  matchMode: GameModeSchema
}).strict();
var AnalyticsAssociationResultSchema = z4.object({ matchContext: AnalyticsMatchContextSchema.nullable() }).strict();
var ClientEnvelopeSchema = AnalyticsClientContextSchema.extend({
  eventId: z4.string().uuid(),
  eventVersion: z4.literal(ANALYTICS_CATALOG_VERSION),
  occurredAt: z4.string().datetime({ offset: true }),
  source: z4.literal("client"),
  analyticsMatchId: z4.string().uuid().optional(),
  analyticsInviteId: z4.string().uuid().optional(),
  rulesVersion: SemanticVersionSchema.optional(),
  mapDigest: z4.string().min(1).max(128).optional(),
  matchMode: GameModeSchema.optional()
}).strict();
function clientEvent(eventName, properties) {
  return ClientEnvelopeSchema.extend({
    eventName: z4.literal(eventName),
    properties
  }).strict();
}
var EntrySurfaceSchema = z4.enum(["landing", "resume", "invite", "tutorial", "rematch"]);
var AppSessionStartedAnalyticsEventSchema = clientEvent(
  "app_session_started",
  z4.object({
    entrySurface: EntrySurfaceSchema,
    launchKind: z4.enum(["cold", "foreground"])
  }).strict()
);
var LandingViewedAnalyticsEventSchema = clientEvent(
  "landing_viewed",
  z4.object({ entrySurface: EntrySurfaceSchema }).strict()
);
var TutorialStartedAnalyticsEventSchema = clientEvent(
  "tutorial_started",
  z4.object({
    tutorialId: StableIdSchema,
    tutorialVersion: SemanticVersionSchema,
    startSurface: z4.enum(["landing", "post_match"])
  }).strict()
);
var TutorialStepCompletedAnalyticsEventSchema = clientEvent(
  "tutorial_step_completed",
  z4.object({
    tutorialId: StableIdSchema,
    tutorialVersion: SemanticVersionSchema,
    stepId: StableIdSchema,
    stepIndex: CountSchema,
    elapsedMs: DurationSchema
  }).strict()
);
var TutorialFinishedAnalyticsEventSchema = clientEvent(
  "tutorial_finished",
  z4.object({
    tutorialId: StableIdSchema,
    tutorialVersion: SemanticVersionSchema,
    exit: z4.enum(["completed", "skipped", "abandoned", "recovered"]),
    completedStepCount: CountSchema,
    elapsedMs: DurationSchema
  }).strict()
);
var MatchExitObservedAnalyticsEventSchema = clientEvent(
  "match_exit_observed",
  z4.object({
    phase: z4.enum(["lobby", "planning", "resolution", "finished"]),
    turnNumber: CountSchema,
    elapsedMs: DurationSchema
  }).strict()
);
var ProductErrorObservedAnalyticsEventSchema = clientEvent(
  "product_error_observed",
  z4.object({
    surface: z4.enum([
      "startup",
      "landing",
      "lobby",
      "match",
      "reconnect",
      "tutorial",
      "store",
      "unknown"
    ]),
    errorCode: ProductAnalyticsErrorCodeSchema,
    severity: z4.enum(["warning", "recoverable", "fatal"]),
    recoverable: z4.boolean()
  }).strict()
);
var FirstUsableScreenMeasuredAnalyticsEventSchema = clientEvent(
  "first_usable_screen_measured",
  z4.object({
    durationMs: DurationSchema,
    entrySurface: EntrySurfaceSchema,
    networkClass: z4.enum(["offline", "slow", "normal", "unknown"])
  }).strict()
);
var MapReadyMeasuredAnalyticsEventSchema = clientEvent(
  "map_ready_measured",
  z4.object({
    durationMs: DurationSchema,
    entryKind: z4.enum(["start", "resume"]),
    assetCache: z4.enum(["warm", "cold", "unknown"])
  }).strict()
);
var ClientHealthSummaryAnalyticsEventSchema = clientEvent(
  "client_health_summary",
  z4.object({
    durationMs: DurationSchema,
    peakMemoryMb: z4.number().min(0).max(1e6).nullable(),
    fatalErrorCount: CountSchema,
    reconnectCount: CountSchema
  }).strict()
);
var StoreViewedAnalyticsEventSchema = clientEvent(
  "store_viewed",
  z4.object({ surface: z4.enum(["menu", "match_end", "promotion"]) }).strict()
);
var ClientProductAnalyticsEventSchema = z4.discriminatedUnion("eventName", [
  AppSessionStartedAnalyticsEventSchema,
  LandingViewedAnalyticsEventSchema,
  TutorialStartedAnalyticsEventSchema,
  TutorialStepCompletedAnalyticsEventSchema,
  TutorialFinishedAnalyticsEventSchema,
  MatchExitObservedAnalyticsEventSchema,
  ProductErrorObservedAnalyticsEventSchema,
  FirstUsableScreenMeasuredAnalyticsEventSchema,
  MapReadyMeasuredAnalyticsEventSchema,
  ClientHealthSummaryAnalyticsEventSchema,
  StoreViewedAnalyticsEventSchema
]);
var ServerEnvelopeSchema = ClientEnvelopeSchema.omit({ source: true }).extend({
  source: z4.literal("server")
});
function serverEvent(eventName, properties) {
  return ServerEnvelopeSchema.extend({
    eventName: z4.literal(eventName),
    properties
  }).strict();
}
var StoreSchema = z4.enum(["web", "apple", "google"]);
var ProductIdSchema = ControlledSlugSchema;
var ServerProductAnalyticsEventSchema = z4.discriminatedUnion("eventName", [
  serverEvent(
    "lobby_created",
    z4.object({ requestedMatchMode: GameModeSchema, botFillCount: CountSchema }).strict()
  ),
  serverEvent(
    "lobby_joined",
    z4.object({
      joinMethod: z4.enum(["code", "deep_link", "rematch"]),
      humanCount: CountSchema,
      botCount: CountSchema
    }).strict()
  ),
  serverEvent(
    "country_selected",
    z4.object({
      countryId: StableIdSchema,
      selectionKind: z4.enum(["first", "change"]),
      noviceSuitability: z4.enum(["recommended", "standard", "advanced"])
    }).strict()
  ),
  serverEvent(
    "match_started",
    z4.object({
      humanCount: CountSchema,
      botCount: CountSchema,
      planningSeconds: CountSchema,
      turnLimit: CountSchema.nullable()
    }).strict()
  ),
  serverEvent(
    "first_meaningful_order_submitted",
    z4.object({ turnNumber: CountSchema, elapsedMs: DurationSchema }).strict()
  ),
  serverEvent(
    "ready_state_changed",
    z4.object({ turnNumber: CountSchema, ready: z4.boolean(), firstReadyOfMatch: z4.boolean() }).strict()
  ),
  serverEvent(
    "match_completed",
    z4.object({
      outcome: z4.enum(["win", "loss", "draw", "eliminated"]),
      placement: z4.number().int().positive().max(1e4),
      turnsPlayed: CountSchema,
      durationMs: DurationSchema,
      humanCount: CountSchema,
      botCount: CountSchema
    }).strict()
  ),
  serverEvent(
    "product_error_observed",
    z4.object({
      surface: z4.enum([
        "startup",
        "landing",
        "lobby",
        "match",
        "reconnect",
        "tutorial",
        "store",
        "unknown"
      ]),
      errorCode: ProductAnalyticsErrorCodeSchema,
      severity: z4.enum(["warning", "recoverable", "fatal"]),
      recoverable: z4.boolean()
    }).strict()
  ),
  serverEvent(
    "invite_created",
    z4.object({
      surface: z4.enum(["lobby", "match_end", "landing"]),
      shareChannel: z4.enum(["copy", "native_share", "unknown"])
    }).strict()
  ),
  serverEvent(
    "invite_opened",
    z4.object({ surface: z4.enum(["web_landing", "app_landing", "store_redirect"]) }).strict()
  ),
  serverEvent(
    "invite_qualified",
    z4.object({ qualification: z4.literal("first_match_completed") }).strict()
  ),
  serverEvent(
    "checkout_started",
    z4.object({
      productId: ProductIdSchema,
      store: StoreSchema,
      priceMinor: CountSchema,
      currency: z4.string().regex(/^[A-Z]{3}$/)
    }).strict()
  ),
  serverEvent(
    "purchase_completed",
    z4.object({
      productId: ProductIdSchema,
      store: StoreSchema,
      priceMinor: CountSchema,
      currency: z4.string().regex(/^[A-Z]{3}$/),
      productKind: z4.enum(["upgrade", "cosmetic", "map", "scenario"])
    }).strict()
  ),
  serverEvent(
    "purchase_failed",
    z4.object({
      productId: ProductIdSchema,
      store: StoreSchema,
      failureCategory: z4.enum([
        "cancelled",
        "declined",
        "verification",
        "store_unavailable",
        "internal"
      ])
    }).strict()
  ),
  serverEvent(
    "refund_recorded",
    z4.object({
      productId: ProductIdSchema,
      store: StoreSchema,
      refundCategory: z4.enum(["requested", "fraud", "chargeback", "other"])
    }).strict()
  )
]);
var ProductAnalyticsEventSchema = z4.union([
  ClientProductAnalyticsEventSchema,
  ServerProductAnalyticsEventSchema
]);
var AnalyticsIngestBatchSchema = z4.object({
  consentPolicyVersion: z4.literal(ANALYTICS_CONSENT_POLICY_VERSION),
  events: z4.array(ClientProductAnalyticsEventSchema).min(1).max(ANALYTICS_MAX_BATCH_EVENTS)
}).strict();

// src/content.ts
import { z as z5 } from "zod";
var PointSchema = z5.tuple([z5.number().finite(), z5.number().finite()]);
var ClosedRingSchema = z5.array(PointSchema).min(4);
var PolygonGeometrySchema = z5.object({
  type: z5.literal("Polygon"),
  coordinates: z5.array(ClosedRingSchema).min(1)
}).strict();
var MultiPolygonGeometrySchema = z5.object({
  type: z5.literal("MultiPolygon"),
  coordinates: z5.array(z5.array(ClosedRingSchema).min(1)).min(1)
}).strict();
var ResourceSchema = z5.enum(["oil", "food", "iron", "rare_materials", "trade_hub"]);
var CountryDefinitionSchema = z5.object({
  id: StableIdSchema,
  nameKey: z5.string().min(1),
  color: z5.string().regex(/^#[0-9A-Fa-f]{6}$/),
  capitalCityId: StableIdSchema,
  selectable: z5.boolean(),
  startingPower: z5.number().int().min(1).max(5),
  difficultyKey: z5.string().min(1)
}).strict();
var StartingUnitsSchema = z5.object({
  infantry: z5.number().int().min(0),
  armor: z5.number().int().min(0),
  air: z5.number().int().min(0),
  navy: z5.number().int().min(0)
}).strict();
var CityPropertiesSchema = z5.object({
  nameKey: z5.string().min(1),
  originalCountryId: StableIdSchema,
  cityPoint: PointSchema,
  labelPoint: PointSchema,
  coastal: z5.boolean(),
  seaZoneIds: z5.array(StableIdSchema),
  development: z5.number().int().min(1),
  population: z5.number().int().min(1),
  fortress: z5.number().int().min(0).max(3),
  resource: ResourceSchema.nullable(),
  startingUnits: StartingUnitsSchema
}).strict();
var CityFeatureSchema = z5.object({
  type: z5.literal("Feature"),
  id: StableIdSchema,
  properties: CityPropertiesSchema,
  geometry: z5.discriminatedUnion("type", [PolygonGeometrySchema, MultiPolygonGeometrySchema])
}).strict();
var CitiesFeatureCollectionSchema = z5.object({
  type: z5.literal("FeatureCollection"),
  features: z5.array(CityFeatureSchema).min(1)
}).strict();
var ConnectionSchema = z5.object({
  id: StableIdSchema,
  a: StableIdSchema,
  b: StableIdSchema,
  type: z5.enum(["land", "amphibious"])
}).strict();
var NavalRoutesSchema = z5.object({
  seaZones: z5.array(z5.object({ id: StableIdSchema, nameKey: z5.string().min(1) }).strict()),
  routes: z5.array(
    z5.object({
      id: StableIdSchema,
      a: StableIdSchema,
      b: StableIdSchema,
      seaZoneIds: z5.array(StableIdSchema).min(1)
    }).strict()
  )
}).strict();
var MapManifestSchema = z5.object({
  schemaVersion: z5.literal(1),
  id: StableIdSchema,
  version: z5.string().regex(/^\d+\.\d+\.\d+$/),
  nameKey: z5.string().min(1),
  descriptionKey: z5.string().min(1),
  recommendedPlayers: z5.object({ min: z5.number().int().min(1), max: z5.number().int().min(1) }),
  supportedPlayers: z5.object({ min: z5.number().int().min(1), max: z5.number().int().min(1) }),
  recommendedTurnLimit: z5.number().int().positive(),
  defaultPlanningSeconds: z5.literal(90),
  bounds: z5.tuple([z5.number(), z5.number(), z5.number(), z5.number()]),
  contentFiles: z5.object({
    countries: z5.string().min(1),
    cities: z5.string().min(1),
    connections: z5.string().min(1),
    navalRoutes: z5.string().min(1),
    balance: z5.string().min(1)
  }).strict(),
  attribution: z5.array(
    z5.object({
      name: z5.string().min(1),
      license: z5.string().min(1),
      url: z5.string().url()
    }).strict()
  ).min(1)
}).strict();
var BalanceCountryMetricSchema = z5.object({
  startingDevelopment: z5.number().int().min(0),
  startingPopulation: z5.number().int().min(0),
  startingMilitaryPower: z5.number().int().min(0),
  startingCapacity: z5.number().int().min(0),
  startingCapacityUsed: z5.number().int().min(0),
  startingIncome: z5.number().int().min(0),
  resourceCounts: z5.partialRecord(ResourceSchema, z5.number().int().min(0)).optional(),
  startingPower: z5.number().int().min(1).max(5).optional(),
  averageConnectivity: z5.number().min(0).optional()
}).strict();
var BalanceSchema = z5.object({
  countryMetrics: z5.record(StableIdSchema, BalanceCountryMetricSchema),
  notes: z5.array(z5.string())
}).strict();
var MapDefinitionSchema = z5.object({
  manifest: MapManifestSchema,
  countries: z5.array(CountryDefinitionSchema).min(2),
  cities: CitiesFeatureCollectionSchema,
  connections: z5.array(ConnectionSchema).min(1),
  navalRoutes: NavalRoutesSchema,
  balance: BalanceSchema
}).strict();

// src/presentation.ts
import { z as z6 } from "zod";
var PresentationBasemapSchema = z6.object({
  schemaVersion: z6.literal(1),
  source: z6.object({
    name: z6.string().min(1),
    url: z6.string().url(),
    license: z6.string().min(1)
  }).strict(),
  bounds: z6.tuple([z6.number(), z6.number(), z6.number(), z6.number()]),
  countries: z6.array(
    z6.object({
      name: z6.string().min(1),
      polygons: z6.array(z6.array(PointSchema).min(3)).min(1)
    }).strict()
  )
}).strict();
var NormalizedPolygonSchema = z6.array(PointSchema).min(4);
var NormalizedLineSchema = z6.array(PointSchema).min(2);
var ProvincePresentationSchema = z6.object({
  schemaVersion: z6.literal(1),
  mapId: z6.string().min(1),
  source: z6.object({
    name: z6.string().min(1),
    url: z6.string().url(),
    license: z6.string().min(1)
  }).strict(),
  bounds: z6.tuple([z6.number(), z6.number(), z6.number(), z6.number()]),
  provinces: z6.array(
    z6.object({
      cityId: z6.string().min(1),
      countryId: z6.string().min(1),
      polygons: z6.array(NormalizedPolygonSchema).min(1)
    }).strict()
  ),
  territoryBorders: z6.array(
    z6.object({
      cityIds: z6.tuple([z6.string().min(1), z6.string().min(1)]),
      lines: z6.array(NormalizedLineSchema).min(1)
    }).strict()
  ),
  adjacency: z6.array(z6.tuple([z6.string().min(1), z6.string().min(1)]))
}).strict();

// src/protocol.ts
import { z as z8 } from "zod";

// src/views.ts
import { z as z7 } from "zod";
var StrengthBandSchema = z7.enum(["none", "small", "medium", "large", "massive"]);
var PublicCityBaseSchema = z7.object({
  cityId: StableIdSchema,
  nameKey: z7.string().min(1),
  ownerCountryId: StableIdSchema,
  originalCountryId: StableIdSchema,
  capital: z7.boolean(),
  coastal: z7.boolean(),
  resource: ResourceSchema.nullable(),
  cityPoint: PointSchema,
  polygon: z7.array(PointSchema).min(4),
  connectionCityIds: z7.array(StableIdSchema),
  landConnectionCityIds: z7.array(StableIdSchema),
  navalRouteCityIds: z7.array(StableIdSchema)
});
var ExactCityViewSchema = PublicCityBaseSchema.extend({
  visibility: z7.literal("exact"),
  units: UnitCountsSchema,
  development: z7.number().int().min(1),
  damagedDevelopmentCeiling: z7.number().int().min(1),
  population: z7.number().int().min(1),
  fortress: z7.number().int().min(0).max(3),
  occupationStage: OccupationStageSchema,
  actionCosts: z7.object({
    developmentTreasury: z7.number().int().min(0),
    fortressTreasury: z7.number().int().min(0),
    production: z7.object({
      infantry: z7.object({ treasury: z7.number().int().min(0), population: z7.literal(1) }).strict(),
      armor: z7.object({ treasury: z7.number().int().min(0), population: z7.literal(1) }).strict(),
      air: z7.object({ treasury: z7.number().int().min(0), population: z7.literal(1) }).strict(),
      navy: z7.object({ treasury: z7.number().int().min(0), population: z7.literal(1) }).strict()
    }).strict()
  }).strict().nullable()
}).strict();
var BandCityViewSchema = PublicCityBaseSchema.extend({
  visibility: z7.literal("band"),
  strengthBand: StrengthBandSchema,
  strengthRange: z7.tuple([z7.number().int().min(0), z7.number().int().min(0)])
}).strict();
var CityViewSchema = z7.discriminatedUnion("visibility", [
  ExactCityViewSchema,
  BandCityViewSchema
]);
var PublicCountryViewSchema = z7.object({
  countryId: StableIdSchema,
  nameKey: z7.string().min(1),
  color: z7.string().regex(/^#[0-9A-Fa-f]{6}$/),
  startingPower: z7.number().int().min(1).max(5),
  difficultyKey: z7.string().min(1)
}).strict();
var PublicPlayerViewSchema = z7.object({
  playerId: OpaqueIdSchema,
  name: z7.string().min(1),
  countryId: StableIdSchema,
  ready: z7.boolean(),
  connected: z7.boolean(),
  eliminated: z7.boolean(),
  reputation: z7.number().int().min(0).max(100)
}).strict();
var ActivePoliticsCardViewSchema = ActivePoliticsCardSchema.extend({
  titleKey: z7.string().min(1),
  bodyKey: z7.string().min(1)
}).strict();
var PlayerViewSchema = z7.object({
  protocolVersion: z7.literal(PROTOCOL_VERSION),
  rulesVersion: z7.literal(RULES_VERSION),
  matchId: OpaqueIdSchema,
  lobbyCode: LobbyCodeSchema,
  gameMode: GameModeSchema,
  turnNumber: z7.number().int().positive(),
  phase: MatchPhaseSchema,
  planningDeadlineMs: z7.number().int().positive().nullable(),
  stateHash: z7.string().min(16),
  deliveryCursor: z7.number().int().min(0),
  me: z7.object({
    playerId: OpaqueIdSchema,
    name: z7.string().min(1),
    countryId: StableIdSchema,
    treasury: z7.number().int().min(0),
    effectiveDevelopmentHalfUnits: z7.number().int().min(0),
    capacity: z7.number().int().min(0),
    usedCapacity: z7.number().int().min(0),
    projectedIncome: z7.number().int().min(0)
  }).strict(),
  countries: z7.array(PublicCountryViewSchema),
  players: z7.array(PublicPlayerViewSchema),
  diplomacy: z7.array(DiplomaticRelationSchema),
  diplomacyProposals: z7.array(DiplomacyProposalSchema),
  politicsOffer: z7.object({
    turnNumber: z7.number().int().positive(),
    cards: z7.array(PoliticsCardSchema).max(3),
    selectedCardId: StableIdSchema.nullable(),
    selectedTargetCityId: StableIdSchema.nullable()
  }).strict().nullable(),
  activePolitics: z7.array(ActivePoliticsCardViewSchema),
  leaderboard: z7.array(
    z7.object({
      countryId: StableIdSchema,
      development: z7.number().int().min(0),
      population: z7.number().int().min(0),
      diplomacy: z7.number().int().min(0),
      militaryPower: z7.discriminatedUnion("visibility", [
        z7.object({ visibility: z7.literal("exact"), value: z7.number().int().min(0) }).strict(),
        z7.object({
          visibility: z7.literal("range"),
          min: z7.number().int().min(0),
          max: z7.number().int().min(0)
        }).strict()
      ]),
      reputation: z7.number().int().min(0).max(100).nullable(),
      eliminated: z7.boolean()
    }).strict()
  ),
  cities: z7.array(CityViewSchema),
  ownOrders: z7.array(OrderSchema),
  outcome: MatchOutcomeSchema.nullable()
}).strict();

// src/protocol.ts
var CommandBaseSchema = z8.object({ clientCommandId: ClientCommandIdSchema });
var SessionCreateCommandSchema = CommandBaseSchema.extend({
  name: GuestNameSchema
}).strict();
var SessionResumeCommandSchema = CommandBaseSchema.extend({
  token: SessionTokenSchema,
  deliveryCursor: z8.number().int().min(0).optional()
}).strict();
var AnalyticsAssociateCommandSchema = CommandBaseSchema.extend({
  consentPolicyVersion: z8.literal(ANALYTICS_CONSENT_POLICY_VERSION),
  context: AnalyticsClientContextSchema
}).strict();
var AnalyticsWithdrawCommandSchema = CommandBaseSchema.strict();
var LobbyCreateCommandSchema = CommandBaseSchema.strict();
var LobbyJoinCommandSchema = CommandBaseSchema.extend({
  code: LobbyCodeSchema
}).strict();
var LobbySelectCountryCommandSchema = CommandBaseSchema.extend({
  code: LobbyCodeSchema,
  countryId: StableIdSchema
}).strict();
var LobbyReadyCommandSchema = CommandBaseSchema.extend({
  code: LobbyCodeSchema,
  ready: z8.boolean()
}).strict();
var LobbySetModeCommandSchema = CommandBaseSchema.extend({
  code: LobbyCodeSchema,
  gameMode: GameModeSchema
}).strict();
var LobbyRerollComputersCommandSchema = CommandBaseSchema.extend({
  code: LobbyCodeSchema
}).strict();
var LobbyStartCommandSchema = CommandBaseSchema.extend({
  code: LobbyCodeSchema
}).strict();
var MatchGetViewCommandSchema = CommandBaseSchema.extend({
  matchId: OpaqueIdSchema
}).strict();
var ClientOrderSchema = z8.discriminatedUnion("kind", [
  MoveOrderSchema.omit({ clientCommandId: true, playerId: true, countryId: true }),
  ProduceOrderSchema.omit({ clientCommandId: true, playerId: true, countryId: true }),
  DevelopOrderSchema.omit({ clientCommandId: true, playerId: true, countryId: true }),
  FortifyOrderSchema.omit({ clientCommandId: true, playerId: true, countryId: true }),
  RetreatOrderSchema.omit({ clientCommandId: true, playerId: true, countryId: true }),
  AirMissionOrderSchema.omit({ clientCommandId: true, playerId: true, countryId: true }),
  NavyMissionOrderSchema.omit({ clientCommandId: true, playerId: true, countryId: true })
]);
var OrdersUpsertCommandSchema = CommandBaseSchema.extend({
  order: ClientOrderSchema
}).strict();
var OrdersRemoveCommandSchema = CommandBaseSchema.extend({
  matchId: OpaqueIdSchema,
  turnNumber: z8.number().int().positive(),
  replacementKey: StableIdSchema
}).strict();
var OrdersSetReadyCommandSchema = CommandBaseSchema.extend({
  matchId: OpaqueIdSchema,
  turnNumber: z8.number().int().positive(),
  ready: z8.boolean()
}).strict();
var DiplomacyProposeCommandSchema = CommandBaseSchema.extend({
  matchId: OpaqueIdSchema,
  targetCountryId: StableIdSchema,
  agreement: DiplomacyAgreementSchema
}).strict();
var DiplomacyRespondCommandSchema = CommandBaseSchema.extend({
  matchId: OpaqueIdSchema,
  proposalId: OpaqueIdSchema,
  accept: z8.boolean()
}).strict();
var DiplomacyDeclareWarCommandSchema = CommandBaseSchema.extend({
  matchId: OpaqueIdSchema,
  targetCountryId: StableIdSchema,
  confirmBetrayal: z8.boolean()
}).strict();
var DiplomacyBreakCommandSchema = CommandBaseSchema.extend({
  matchId: OpaqueIdSchema,
  targetCountryId: StableIdSchema,
  agreements: z8.array(DiplomacyAgreementSchema).min(1).max(3),
  confirmBetrayal: z8.boolean()
}).strict();
var PoliticsSelectCommandSchema = CommandBaseSchema.extend({
  matchId: OpaqueIdSchema,
  cardId: StableIdSchema.nullable(),
  targetCityId: StableIdSchema.nullable()
}).strict();
var ErrorCodeSchema = z8.enum([
  "invalid_payload",
  "not_authenticated",
  "not_authorized",
  "not_found",
  "conflict",
  "invalid_phase",
  "invalid_order",
  "duplicate",
  "internal"
]);
var CommandAckSchema = z8.discriminatedUnion("ok", [
  z8.object({ ok: z8.literal(true), data: z8.record(z8.string(), z8.unknown()).optional() }).strict(),
  z8.object({
    ok: z8.literal(false),
    code: ErrorCodeSchema,
    messageKey: z8.string().min(1),
    params: z8.record(z8.string(), z8.union([z8.string(), z8.number(), z8.boolean()])).optional()
  }).strict()
]);
var SessionEstablishedSchema = z8.object({
  protocolVersion: z8.literal(PROTOCOL_VERSION),
  playerId: OpaqueIdSchema,
  name: GuestNameSchema,
  token: SessionTokenSchema
}).strict();
var LobbyPlayerSchema = z8.object({
  playerId: OpaqueIdSchema,
  name: GuestNameSchema,
  countryId: StableIdSchema.nullable(),
  ready: z8.boolean(),
  host: z8.boolean()
}).strict();
var LobbyViewSchema = z8.object({
  protocolVersion: z8.literal(PROTOCOL_VERSION),
  code: LobbyCodeSchema,
  gameMode: GameModeSchema,
  players: z8.array(LobbyPlayerSchema),
  computers: z8.array(
    z8.object({
      countryId: StableIdSchema
    }).strict()
  ).max(3),
  availableCountries: z8.array(
    z8.object({
      countryId: StableIdSchema,
      nameKey: z8.string().min(1),
      color: z8.string(),
      startingPower: z8.number().int().min(1).max(5),
      difficultyKey: z8.string().min(1),
      metrics: z8.object({
        development: z8.number().int().min(0),
        population: z8.number().int().min(0),
        military: z8.number().int().min(0)
      }).strict(),
      selectedByPlayerId: OpaqueIdSchema.nullable()
    }).strict()
  ),
  autoFillCount: z8.number().int().min(0).max(3),
  canStart: z8.boolean()
}).strict();
var ResolutionEventsPayloadSchema = z8.object({
  protocolVersion: z8.literal(PROTOCOL_VERSION),
  matchId: OpaqueIdSchema,
  turnNumber: z8.number().int().positive(),
  fromCursor: z8.number().int().min(0),
  toCursor: z8.number().int().min(0),
  events: z8.array(PlayerEventSchema)
}).strict();
var ServerErrorPayloadSchema = z8.object({
  code: ErrorCodeSchema,
  messageKey: z8.string().min(1)
}).strict();
var ServerPayloadSchemas = {
  "session.established": SessionEstablishedSchema,
  "lobby.updated": LobbyViewSchema,
  "match.view": PlayerViewSchema,
  "resolution.events": ResolutionEventsPayloadSchema,
  error: ServerErrorPayloadSchema
};
export {
  ANALYTICS_CATALOG_VERSION,
  ANALYTICS_CONSENT_POLICY_VERSION,
  ANALYTICS_MAX_BATCH_EVENTS,
  ActivePoliticsCardSchema,
  ActivePoliticsCardViewSchema,
  AirMissionOrderSchema,
  AirMissionTypeSchema,
  AnalyticsAcquisitionSourceSchema,
  AnalyticsAssociateCommandSchema,
  AnalyticsAssociationResultSchema,
  AnalyticsClientContextSchema,
  AnalyticsEnvironmentSchema,
  AnalyticsIngestBatchSchema,
  AnalyticsMatchContextSchema,
  AnalyticsPlatformSchema,
  AnalyticsWithdrawCommandSchema,
  AppSessionStartedAnalyticsEventSchema,
  AuthoritativeEventSchema,
  BalanceCountryMetricSchema,
  BalanceSchema,
  BandCityViewSchema,
  BattleEventSchema,
  BattleSideSchema,
  CaptureEventSchema,
  CitiesFeatureCollectionSchema,
  CityFeatureSchema,
  CityPropertiesSchema,
  CityStateSchema,
  CityViewSchema,
  ClientCommandIdSchema,
  ClientHealthSummaryAnalyticsEventSchema,
  ClientOrderSchema,
  ClientProductAnalyticsEventSchema,
  CommandAckSchema,
  ConnectionSchema,
  CountryDefinitionSchema,
  DevelopOrderSchema,
  DevelopmentEventSchema,
  DiplomacyAgreementSchema,
  DiplomacyBreakCommandSchema,
  DiplomacyDeclareWarCommandSchema,
  DiplomacyProposalSchema,
  DiplomacyProposeCommandSchema,
  DiplomacyRespondCommandSchema,
  DiplomaticRelationSchema,
  EliminationEventSchema,
  ErrorCodeSchema,
  ExactCityViewSchema,
  FirstUsableScreenMeasuredAnalyticsEventSchema,
  FortificationEventSchema,
  FortifyOrderSchema,
  GameModeSchema,
  GuestNameSchema,
  IncomeEventSchema,
  LandUnitTypeSchema,
  LandUnitsSchema,
  LandingViewedAnalyticsEventSchema,
  LobbyCodeSchema,
  LobbyCreateCommandSchema,
  LobbyJoinCommandSchema,
  LobbyPlayerSchema,
  LobbyReadyCommandSchema,
  LobbyRerollComputersCommandSchema,
  LobbySelectCountryCommandSchema,
  LobbySetModeCommandSchema,
  LobbyStartCommandSchema,
  LobbyViewSchema,
  MapDefinitionSchema,
  MapManifestSchema,
  MapReadyMeasuredAnalyticsEventSchema,
  MatchExitObservedAnalyticsEventSchema,
  MatchGetViewCommandSchema,
  MatchOutcomeSchema,
  MatchPhaseSchema,
  MatchPlayerSchema,
  MatchStateSchema,
  MissionBattleEventSchema,
  MissionEventSchema,
  MoveOrderSchema,
  MultiPolygonGeometrySchema,
  NavalRoutesSchema,
  NavyMissionOrderSchema,
  NavyMissionTypeSchema,
  OccupationStageSchema,
  OpaqueIdSchema,
  OrderSchema,
  OrdersRemoveCommandSchema,
  OrdersSetReadyCommandSchema,
  OrdersUpsertCommandSchema,
  PROTOCOL_VERSION,
  PlayerEventSchema,
  PlayerViewSchema,
  PointSchema,
  PoliticsCardSchema,
  PoliticsCatalogSchema,
  PoliticsComparisonSchema,
  PoliticsEffectSchema,
  PoliticsFactNameSchema,
  PoliticsFactValueSchema,
  PoliticsFactsSchema,
  PoliticsHistoryEntrySchema,
  PoliticsOfferSchema,
  PoliticsPredicateSchema,
  PoliticsSelectCommandSchema,
  PoliticsTargetSchema,
  PolygonGeometrySchema,
  PresentationBasemapSchema,
  ProduceOrderSchema,
  ProductAnalyticsErrorCodeSchema,
  ProductAnalyticsEventSchema,
  ProductErrorObservedAnalyticsEventSchema,
  ProductionEventSchema,
  ProvincePresentationSchema,
  PublicCountryViewSchema,
  PublicPlayerViewSchema,
  RULES_VERSION,
  RefundEventSchema,
  ReinforcementEventSchema,
  ResolutionEventsPayloadSchema,
  ResourceSchema,
  RetreatOrderSchema,
  ServerErrorPayloadSchema,
  ServerPayloadSchemas,
  ServerProductAnalyticsEventSchema,
  SessionCreateCommandSchema,
  SessionEstablishedSchema,
  SessionResumeCommandSchema,
  SessionTokenSchema,
  StableIdSchema,
  StartingUnitsSchema,
  StoreViewedAnalyticsEventSchema,
  StrengthBandSchema,
  TutorialFinishedAnalyticsEventSchema,
  TutorialStartedAnalyticsEventSchema,
  TutorialStepCompletedAnalyticsEventSchema,
  UnitCountsSchema,
  UnitTypeSchema,
  VictoryStandingSchema
};
